import { FC, useMemo } from "react";
import {
  CONFLICT_ALERT_TIMELINE,
  SMS_DELIVERY_LOG_TYPE,
} from "../types/CommonTypes";
import formatDateTime from "../../../helpers/formatDateTime";
import { Box, Typography } from "@mui/material";
import { globalStyles } from "../../../styles/GlobalStyles";
import Icons from "../../../assets/Icons";
import { useTranslation } from "react-i18next";
import NoteChip from "./NoteChip";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";

interface ConflictAlertTimelineProps {
  item: CONFLICT_ALERT_TIMELINE;
  hideLine?: boolean;
  isNotLast?: boolean;
}

const ConflictAlertTimeline: FC<ConflictAlertTimelineProps> = ({
  item,
  hideLine = false,
  isNotLast = false,
}) => {
  const { t } = useTranslation();

  const { conflictType, sentTo, createdAt }: CONFLICT_ALERT_TIMELINE = item;

  const title = useMemo(() => {
    switch (conflictType) {
      case SMS_DELIVERY_LOG_TYPE.ANESTHETIST_NEXT_CASE_IMPACT:
        return t("Anesthelogist_Conflict_Alert_Text", { send_to: sentTo });

      case SMS_DELIVERY_LOG_TYPE.SURGEON_NEXT_CASE_IMPACT:
        return t("Surgeon_Conflict_Alert_Text", { send_to: sentTo });

      default:
        return t("Equipment_Conflict_Alert_Text", { send_to: sentTo });
    }
  }, [conflictType, sentTo, t]);

  return (
    <Box sx={globalStyles.row}>
      {!hideLine && (
        <Box sx={styles.timelineColumn}>
          <Box
            border={1}
            borderRadius={3}
            borderColor="custom.border.default"
            width={18}
            height={18}
            m={0.25}
            style={globalStyles.center}
          >
            <Icons.SmsFilled width={12} height={12} />
          </Box>
          {isNotLast && <Box sx={styles.line} />}
        </Box>
      )}

      <Box
        sx={[
          styles.card,
          !hideLine && isNotLast && styles.cardSpacing,
          hideLine && styles.compactCard,
        ]}
      >
        <Box sx={styles.headerRow}>
          <Typography sx={styles.title}>{t("Conflict_Alert")}</Typography>
        </Box>

        <Typography sx={styles.description}>{title}</Typography>

        <Typography sx={styles.metaText}>
          {t("SMS_sent_at", {
            sent_at: formatDateTime(createdAt, true, "hh:mm:ss aaa"),
          })}
        </Typography>

        <Box sx={styles.chipContainer}>
          <NoteChip
            icon={<ChatBubbleOutlineOutlinedIcon sx={{ fontSize: 16 }} />}
            label={t("SMS")}
          />
        </Box>
      </Box>
    </Box>
  );
};

const styles = {
  timelineColumn: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    mr: "12px",
  },

  line: {
    width: "4px",
    flex: 1,
    borderRadius: "4px",
    backgroundColor: "custom.background.activity",
  },

  card: {
    flex: 1,
    minWidth: 0,
    minHeight: "88px",

    backgroundColor: "custom.background.primary",

    border: "1px solid #E5E7EB",
    borderRadius: "8px",

    px: "12px",
    py: "10px",

    boxSizing: "border-box",

    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },

  cardSpacing: {
    mb: "16px",
  },

  compactCard: {
    width: "100%",
  },

  headerRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",

    columnGap: "6px",
    rowGap: "4px",

    width: "100%",
  },

  title: {
    fontSize: "16px",
    lineHeight: "22px",
    fontWeight: 700,

    color: "custom.foreground.primary",
  },

  description: {
    mt: "3px",

    fontSize: "15px",
    lineHeight: "20px",
    fontWeight: 400,

    color: "custom.foreground.attention",

    width: "100%",
    maxWidth: "100%",

    whiteSpace: "normal",
    overflowWrap: "anywhere",
  },

  metaText: {
    mt: "3px",

    fontSize: "12px",
    lineHeight: "16px",

    color: "custom.foreground.secondary",
  },

  chipContainer: {
    mt: "6px",

    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
  },
};

export default ConflictAlertTimeline;
